// Per-item logo placement editor (Tier 4 — SanMar images).
//
// Every cart item has a SanMar product image (the actual photo of
// the color the customer chose). We use that as the placement
// mockup directly: front view comes from item.colorImage, back
// view is derived by URL-swapping `_model_front` → `_model_back`.
// No garment-type detection, no canonical mockup PNGs, no flat
// silhouettes — the customer drops their logo on a real photo of
// the product they actually picked.
//
// Front and Back are "drag sides" — they get a real product photo
// + draggable marker. Left Side / Right Side don't have dedicated
// SanMar photos, so they always render as a labeled text input
// where the customer describes placement in words ("centered on
// left sleeve, 3 inches wide"). Storage shape per item:
//   - drag mode:  { markers: [...], notes: { leftSide?, rightSide? } }
//   - text mode:  string (whole-item description; legacy fallback)
//
// Items where neither front nor back image loads (rare) fall back
// to a per-item textarea so the customer can describe placement
// in words.

import React, { useLayoutEffect, useRef, useState } from 'react';
import { Rnd } from 'react-rnd';
import { getFrontUrl, getBackUrl, isLabeledFront } from '../utils/sanmarImages';
import './PlacementEditor.css';

const FALLBACK_ASPECT = 1.5;

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}

// Default starting placements. Full Front / Back are larger crests;
// Left Chest and Right Chest are small monograms anchored over the
// chest area of the same front photo. Customer drags from there.
const DEFAULT_FRONT       = { xPct: 35, yPct: 30, widthPct: 25 };
const DEFAULT_BACK        = { xPct: 32, yPct: 22, widthPct: 35 };
const DEFAULT_LEFT_CHEST  = { xPct: 22, yPct: 22, widthPct: 12 };
const DEFAULT_RIGHT_CHEST = { xPct: 66, yPct: 22, widthPct: 12 };
// Sleeves / sides — narrower, centered vertically. Reasonable
// defaults for the side-card canvases (which currently show the
// front photo as a stopgap, since SanMar doesn't expose dedicated
// sleeve photos).
const DEFAULT_LEFT_SIDE   = { xPct: 10, yPct: 35, widthPct: 12 };
const DEFAULT_RIGHT_SIDE  = { xPct: 78, yPct: 35, widthPct: 12 };

// Side keys used by both Quote.js and this component. The four "drag"
// sides (front/back/leftChest/rightChest) get real product photos and
// drop-and-drag markers; the two "text" sides (leftSide/rightSide)
// collect a free-text placement description.
const SIDE_KEYS = ['front', 'back', 'leftChest', 'rightChest', 'leftSide', 'rightSide'];
const SIDE_LABELS = {
  front: 'FULL FRONT',
  back: 'FULL BACK',
  leftChest: 'LEFT CHEST',
  rightChest: 'RIGHT CHEST',
  leftSide: 'LEFT PANEL / SLEEVE',
  rightSide: 'RIGHT PANEL / SLEEVE',
};
const SIDE_DISPLAY = {
  front: 'Full Front',
  back: 'Full Back',
  leftChest: 'Left Chest',
  rightChest: 'Right Chest',
  leftSide: 'Left Panel / Sleeve',
  rightSide: 'Right Panel / Sleeve',
};
const DEFAULTS_BY_SIDE = {
  front: DEFAULT_FRONT,
  back: DEFAULT_BACK,
  leftChest: DEFAULT_LEFT_CHEST,
  rightChest: DEFAULT_RIGHT_CHEST,
  leftSide: DEFAULT_LEFT_SIDE,
  rightSide: DEFAULT_RIGHT_SIDE,
};

// All four drag sides render on the actual SanMar product photo.
// Front and the two chest positions share the front image (multiple
// markers stack on the same canvas). Back uses the back image.
// leftSide / rightSide (sleeves) are description-only — SanMar
// doesn't expose dedicated sleeve photos.
function isDragSide(side) {
  return side === 'front' || side === 'back' || side === 'leftChest' || side === 'rightChest';
}


// Normalize per-item placement to { markers, notes }.
//   - Plain array → { markers: array, notes: {} }
//   - { markers, notes } → use as-is (defensive copy of notes)
//   - Legacy zone-keyed object { front: [..], back: [..] } → flatten markers, no notes
//   - Single marker { xPct, ... } → wrap as one-marker array
//   - String → text mode (returned as null markers/notes; caller handles)
function parseItemPlacement(input) {
  if (input == null) return { markers: [], notes: {} };
  if (typeof input === 'string') return { markers: [], notes: {} };
  if (Array.isArray(input)) return { markers: input.slice(), notes: {} };
  if (typeof input === 'object') {
    // New shape: { markers, notes }
    if (Array.isArray(input.markers)) {
      const notes = (input.notes && typeof input.notes === 'object') ? { ...input.notes } : {};
      return { markers: input.markers.slice(), notes };
    }
    // Single-marker object
    if ('xPct' in input) return { markers: [input], notes: {} };
    // Legacy zone-keyed: flatten any side arrays/objects to a marker list,
    // tagging the marker's `side` from the key.
    const markers = [];
    for (const [key, val] of Object.entries(input)) {
      const list = Array.isArray(val) ? val : (val && typeof val === 'object' && 'xPct' in val ? [val] : []);
      for (const m of list) {
        if (m && typeof m === 'object' && 'xPct' in m) {
          markers.push({ ...m, side: m.side || key });
        }
      }
    }
    return { markers, notes: {} };
  }
  return { markers: [], notes: {} };
}

// Back-compat wrapper for PlacementCanvas (still expects an array).
function normalizePlacements(input) {
  if (!input) return [];
  if (Array.isArray(input)) return input;
  if (typeof input === 'object') {
    if ('xPct' in input) return [input];
    if (Array.isArray(input.markers)) return input.markers.slice();
    return Object.values(input)
      .flat()
      .filter(m => m && typeof m === 'object' && 'xPct' in m);
  }
  return [];
}

// One placement card = one product photo + draggable marker(s).
function PlacementCanvas({ src, label, logo, placements, defaultPlacement, maxMarkers, onChange, onImageError, onDismiss }) {
  const stageRef = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const markers = normalizePlacements(placements);

  useLayoutEffect(() => {
    if (!stageRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0].contentRect;
      setSize({ w: r.width, h: r.height });
    });
    ro.observe(stageRef.current);
    return () => ro.disconnect();
  }, []);

  const aspectRatio = logo?.aspectRatio || FALLBACK_ASPECT;
  // Stage uses a fixed aspect ratio so every card across the editor
  // renders at the same size, regardless of whether the SanMar image
  // is portrait apparel, square bag, or landscape hat. The image
  // scales inside via CSS object-fit: contain.

  function updateMarker(index, x, y, w) {
    if (size.w === 0) return;
    const next = markers.slice();
    next[index] = {
      xPct: clamp((x / size.w) * 100, 0, 100),
      yPct: clamp((y / size.h) * 100, 0, 100),
      widthPct: clamp((w / size.w) * 100, 2, 95),
    };
    onChange(next);
  }

  function addMarker() {
    const base = markers[markers.length - 1] || defaultPlacement;
    const next = markers.slice();
    next.push({
      xPct: clamp(base.xPct + 8, 0, 80),
      yPct: clamp(base.yPct + 8, 0, 80),
      widthPct: base.widthPct,
    });
    onChange(next);
  }

  function removeMarker(index) {
    const next = markers.slice();
    next.splice(index, 1);
    onChange(next);
  }

  const displayMarkers = markers.length === 0 ? [defaultPlacement] : markers;
  const hasRealMarkers = markers.length > 0;
  const canAddMore = (maxMarkers || 1) > markers.length && hasRealMarkers;

  return (
    <div className="placement-canvas">
      <div className="placement-canvas-header">
        {label && <div className="placement-zone-label">{label}</div>}
        {onDismiss && (
          <button
            type="button"
            className="placement-side-dismiss"
            onClick={onDismiss}
            aria-label={`Remove ${label || 'this'} side`}
            title={`Remove ${label || 'this'} side`}
          >
            &times;
          </button>
        )}
      </div>
      <div className="placement-stage" ref={stageRef}>
        <div className="placement-stage-bg">
          <img
            className="placement-mockup-img"
            src={src}
            alt=""
            draggable={false}
            onError={onImageError}
            onLoad={(e) => {
              // SanMar serves a generic "IMAGE NOT YET AVAILABLE FOR
              // SELECTED COLOR" placeholder when the requested back
              // (or front) variant doesn't exist for that product. The
              // placeholder still loads with HTTP 200, so onError never
              // fires — but its natural dimensions are much smaller
              // (~200x250) than a real product photo (500+x600+). Treat
              // anything under ~320px wide as a placeholder so we can
              // hide the card entirely.
              const w = e.target.naturalWidth || 0;
              const h = e.target.naturalHeight || 0;
              if (w > 0 && (w < 320 || h < 320) && onImageError) {
                onImageError();
              }
            }}
          />
        </div>
        {size.w > 0 && displayMarkers.map((p, i) => {
          const widthPx = (p.widthPct / 100) * size.w;
          const heightPx = widthPx / aspectRatio;
          const xPx = (p.xPct / 100) * size.w;
          const yPx = (p.yPct / 100) * size.h;
          return (
            <Rnd
              key={hasRealMarkers ? i : 'preview'}
              className="placement-rect"
              size={{ width: widthPx, height: heightPx }}
              position={{ x: xPx, y: yPx }}
              lockAspectRatio={aspectRatio}
              bounds="parent"
              minWidth={6}
              minHeight={6}
              // Only the 4 corners resize. Edges stay free for
              // dragging the marker around — important when the
              // logo shrinks small enough that handles would
              // otherwise cover the whole body.
              enableResizing={{
                top: false, right: false, bottom: false, left: false,
                topLeft: true, topRight: true, bottomLeft: true, bottomRight: true,
              }}
              // Invisible corner handles, kept slightly outside the
              // marker bounds so the body of the marker stays fully
              // draggable. Resize still works — there's just no
              // visible dot covering the logo.
              resizeHandleStyles={{
                topLeft:     { width: 12, height: 12, top: -6,  left:  -6 },
                topRight:    { width: 12, height: 12, top: -6,  right: -6 },
                bottomLeft:  { width: 12, height: 12, bottom: -6, left:  -6 },
                bottomRight: { width: 12, height: 12, bottom: -6, right: -6 },
              }}
              onDragStop={(e, d) => {
                if (!hasRealMarkers) {
                  onChange([{
                    xPct: clamp((d.x / size.w) * 100, 0, 100),
                    yPct: clamp((d.y / size.h) * 100, 0, 100),
                    widthPct: clamp((widthPx / size.w) * 100, 2, 95),
                  }]);
                } else {
                  updateMarker(i, d.x, d.y, widthPx);
                }
              }}
              onResizeStop={(e, dir, ref, delta, pos) => {
                if (!hasRealMarkers) {
                  onChange([{
                    xPct: clamp((pos.x / size.w) * 100, 0, 100),
                    yPct: clamp((pos.y / size.h) * 100, 0, 100),
                    widthPct: clamp((ref.offsetWidth / size.w) * 100, 2, 95),
                  }]);
                } else {
                  updateMarker(i, pos.x, pos.y, ref.offsetWidth);
                }
              }}
            >
              {logo?.previewUrl ? (
                <img src={logo.previewUrl} alt="logo placement preview" className="placement-logo" />
              ) : (
                <div className="placement-logo-box">
                  <span>LOGO</span>
                </div>
              )}
            </Rnd>
          );
        })}
      </div>
      {(hasRealMarkers || canAddMore) && (
        <div className="placement-canvas-footer">
          {hasRealMarkers && markers.map((m, i) => (
            <button
              key={i}
              type="button"
              className="placement-marker-chip"
              onClick={() => removeMarker(i)}
              title={`Remove logo ${i + 1}`}
              aria-label={`Remove logo ${i + 1}`}
            >
              <span className="placement-marker-chip-label">Logo {i + 1}</span>
              <span className="placement-marker-chip-x" aria-hidden="true">&times;</span>
            </button>
          ))}
          {canAddMore && (
            <button
              type="button"
              className="placement-add-marker"
              onClick={addMarker}
            >
              + Add another logo
            </button>
          )}
        </div>
      )}
      {hasRealMarkers && markers.length === 1 && (
        <div className="placement-readout">
          {markers[0].xPct.toFixed(0)}% from left &middot; {markers[0].yPct.toFixed(0)}% from top &middot; {markers[0].widthPct.toFixed(0)}% wide
        </div>
      )}
    </div>
  );
}

// Header used by every placement-item card. Shows the product
// thumbnail (same colorImage we'll use as the mockup) plus name +
// color + category chip.
function ItemHeader({ item }) {
  return (
    <div className="placement-item-header">
      {item.colorImage && (
        <img
          className="placement-item-thumb"
          src={item.colorImage}
          alt=""
          loading="lazy"
        />
      )}
      <div className="placement-item-name">
        <strong>{item.name}</strong>
        {item.color && <span className="placement-item-meta"> &middot; {item.color}</span>}
      </div>
      {item.category && (
        <div className="placement-item-type">{item.category}</div>
      )}
    </div>
  );
}

// Decide which side cards to render based on the item's per-side
// logo assignments. A side is active if there's a logo assigned to
// it. No assignments at all → render nothing for this item.
function getActiveSides(item) {
  return {
    front:      !!item._frontLogo,
    back:       !!item._backLogo,
    leftChest:  !!item._leftChestLogo,
    rightChest: !!item._rightChestLogo,
    leftSide:   !!item._leftSideLogo,
    rightSide:  !!item._rightSideLogo,
  };
}

// Inline assignment row: lets the customer change which logo goes on
// front / back without leaving the placement card. Wired through from
// the parent so updates flow back into the cart's logoAssignment state.
function ItemAssignmentRow({ item, itemIndex, logos, onAssignLogo }) {
  if (!Array.isArray(logos) || logos.length === 0) return null;
  const fa = item.logoAssignment || {};
  // Per-product decoration restriction. e.g. Polos are Embroidery-only
  // by Heaven Sent policy — a Screen Print logo can't be applied to a
  // polo. Filter the dropdown options accordingly so the customer
  // can't even pick an unsupported logo here.
  const allowed = Array.isArray(item.allowedDecorations) && item.allowedDecorations.length > 0
    ? new Set(item.allowedDecorations)
    : null;
  const isAllowedForItem = (logo) => !allowed || allowed.has(logo.decoration);
  const describe = (logo, idx) => {
    const tag = logo.decoration === 'Screen Print'
      ? `${logo.colors || 0}-color Screen Print`
      : (logo.decoration || 'Logo');
    return `Logo ${idx + 1}: ${tag}`;
  };
  // Only render a side's dropdown if at least one logo claims it AND
  // is allowed by this product. Sides with no matching logo are
  // hidden entirely — fewer empty dropdowns to scan past.
  const sidesWithLogos = SIDE_KEYS.filter(side =>
    logos.some(l => (l.side || 'front') === side && isAllowedForItem(l))
  );
  if (sidesWithLogos.length === 0) return null;
  return (
    <div className="placement-item-assignment">
      {sidesWithLogos.map(side => {
        // Include each logo's original index so the visible
        // numbering ("Logo 3") stays 1-based by creation order, not
        // by side. The filtered options are limited to logos for this
        // specific side AND allowed by the item's allowedDecorations.
        const options = logos
          .map((logo, li) => ({ logo, li }))
          .filter(({ logo }) => (logo.side || 'front') === side && isAllowedForItem(logo));
        return (
          <div key={side} className="item-assignment-side">
            <label className="modal-label-sub">{SIDE_DISPLAY[side]}</label>
            <select
              className="item-assignment-select"
              value={fa[side] || ''}
              onChange={e => onAssignLogo && onAssignLogo(itemIndex, side, e.target.value || null)}
            >
              <option value="">None</option>
              {options.map(({ logo, li }) => (
                <option key={logo.id} value={logo.id}>{describe(logo, li)}</option>
              ))}
            </select>
          </div>
        );
      })}
    </div>
  );
}

function ItemPlacementSection({ item, itemIndex, placements, onChange, logos, onAssignLogo, logoWidths }) {
  // Build per-side logo objects with previewUrl from the assigned logo.
  // We do aspect-ratio resolution lazily inside this component instead
  // of in the parent so each item can use its own assigned logos.
  const logoBySide = {
    front:      item._frontLogo      ? { previewUrl: item._frontLogo.fileUrl }      : null,
    back:       item._backLogo       ? { previewUrl: item._backLogo.fileUrl }       : null,
    leftChest:  item._leftChestLogo  ? { previewUrl: item._leftChestLogo.fileUrl }  : null,
    rightChest: item._rightChestLogo ? { previewUrl: item._rightChestLogo.fileUrl } : null,
    leftSide:   item._leftSideLogo   ? { previewUrl: item._leftSideLogo.fileUrl }   : null,
    rightSide:  item._rightSideLogo  ? { previewUrl: item._rightSideLogo.fileUrl }  : null,
  };
  const frontUrl = getFrontUrl(item);
  const backUrl  = getBackUrl(item);
  // Chest positions piggyback on the front photo. Sleeves stay text-only.
  const urlBySide = {
    front: frontUrl,
    back: backUrl,
    leftChest: frontUrl,
    rightChest: frontUrl,
    leftSide: null,
    rightSide: null,
  };
  const [backFailed, setBackFailed]   = useState(false);
  const [frontFailed, setFrontFailed] = useState(false);
  const failedBySide = {
    front: frontFailed,
    back: backFailed,
    leftChest: frontFailed,
    rightChest: frontFailed,
    leftSide: false,
    rightSide: false,
  };
  const setFailedBySide = {
    front: setFrontFailed,
    back: setBackFailed,
    leftChest: setFrontFailed,
    rightChest: setFrontFailed,
    leftSide: () => {},
    rightSide: () => {},
  };
  // Sides the customer has explicitly dismissed via the × on the
  // card header. Lets the customer hide the side they don't want a
  // logo preview for (and bring it back via the "+ Add" button).
  const [dismissedSides, setDismissedSides] = useState(() => new Set());

  const sides = getActiveSides(item);
  const isDragShown = (side) =>
    isDragSide(side) && sides[side] && urlBySide[side] && !failedBySide[side] && !dismissedSides.has(side);
  // Side-note inputs render whenever the side is active; no canvas, no
  // image dependency. Dismissal doesn't apply to text-only sides.
  const showLeftSide  = sides.leftSide;
  const showRightSide = sides.rightSide;
  // Drag mode is "available" only when at least one drag-side has a
  // usable photo. Items where the only active sides are leftSide /
  // rightSide stay locked to text-only side-notes (no toggle).
  const dragAvailable = SIDE_KEYS.some(side =>
    isDragSide(side) && sides[side] && urlBySide[side] && !failedBySide[side]
  );

  // Mode = string → whole-item text. Otherwise drag/mixed. We default
  // to drag/mixed for every new item so customers see canvases on
  // load instead of a wall of textareas — text mode is opt-in via the
  // tab toggle. The drag-mode branch handles items that have only
  // sleeve/side assignments (no front/back) by rendering only the
  // text inputs for those sides; no need to fall back to legacy
  // text-only mode just because drag canvases aren't available.
  const isTextMode = typeof placements === 'string';
  const switchToText = () => onChange('');
  const switchToDrag = () => onChange({ markers: [], notes: {} });

  // Segmented mode toggle (only rendered if drag mode is actually
  // available — items with no product photo stay locked to text).
  const modeTabs = dragAvailable ? (
    <div className="placement-mode-tabs" role="tablist">
      <button
        type="button"
        role="tab"
        aria-selected={!isTextMode}
        className={`placement-mode-tab${!isTextMode ? ' is-active' : ''}`}
        onClick={switchToDrag}
      >
        Drag on photo
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={isTextMode}
        className={`placement-mode-tab${isTextMode ? ' is-active' : ''}`}
        onClick={switchToText}
      >
        Describe in text
      </button>
    </div>
  ) : null;

  if (isTextMode) {
    // In text mode we surface ONE labeled textarea per active side
    // that has a logo assigned, so each logo gets its own placement
    // description rather than sharing a single catch-all field. If
    // the item has zero logos assigned (mid-edit), fall back to a
    // single generic textarea so the customer isn't staring at a
    // header with no input below it.
    const parsedTM = parseItemPlacement(placements);
    const notesTM = (placements && typeof placements === 'object') ? parsedTM.notes : {};
    const legacyText = typeof placements === 'string' ? placements : '';
    const activeSidesTM = SIDE_KEYS.filter(s => sides[s]);
    const setNoteForSide = (side, text) => {
      const nextNotes = { ...notesTM };
      if (text) nextNotes[side] = text;
      else delete nextNotes[side];
      onChange({ markers: [], notes: nextNotes });
    };
    return (
      <div className="placement-item placement-item-skip">
        <ItemHeader item={item} />
        {/* Always render the assignment dropdowns, even in text-only
            mode. Otherwise a customer who toggles every side to None
            (or switches to "Describe in text") has no way back to
            picking a logo. */}
        <ItemAssignmentRow item={item} itemIndex={itemIndex} logos={logos} onAssignLogo={onAssignLogo} />
        {modeTabs}
        {activeSidesTM.length > 0 ? (
          <div className="placement-side-notes">
            {activeSidesTM.map(side => {
              const logo = side === 'front' ? item._frontLogo
                : side === 'back' ? item._backLogo
                : side === 'leftChest' ? item._leftChestLogo
                : side === 'rightChest' ? item._rightChestLogo
                : side === 'leftSide' ? item._leftSideLogo
                : item._rightSideLogo;
              const tag = logo?.decoration === 'Screen Print'
                ? `${logo.colors || 0}-color Screen Print`
                : (logo?.decoration || 'Logo');
              const value = notesTM[side] || (side === 'front' && !notesTM.front ? legacyText : '');
              return (
                <div key={side} className="placement-side-note">
                  <label className="placement-side-note-label">
                    {SIDE_DISPLAY[side]} &mdash; {tag} placement
                  </label>
                  <textarea
                    className="placement-side-note-input"
                    rows={2}
                    maxLength={300}
                    value={value}
                    placeholder={`e.g. "centered on ${SIDE_DISPLAY[side].toLowerCase()}, 3 inches wide"`}
                    onChange={(e) => setNoteForSide(side, e.target.value)}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          // Fallback: no logos assigned yet — show a single generic
          // box so the textarea doesn't disappear entirely.
          <>
            <label className="placement-item-skip-label">Logo placement for this item</label>
            <textarea
              className="placement-item-skip-input"
              rows={2}
              maxLength={300}
              value={legacyText}
              placeholder='e.g. "left sleeve, 3 inches wide, centered" or "right side seam"'
              onChange={(e) => onChange(e.target.value)}
            />
          </>
        )}
      </div>
    );
  }

  // Markers are split per side in storage so the worker can tell
  // which surface each one belongs to. The editor stores them as a
  // single flat array of markers + a side-keyed `notes` object for
  // text-only sides; we split markers by `side` field, defaulting to
  // "front" if missing (legacy data shape).
  const parsed = parseItemPlacement(placements);
  const allMarkers = parsed.markers;
  const allNotes   = parsed.notes;
  const markersBySide = {
    front:      allMarkers.filter(m => (m.side || 'front') === 'front'),
    back:       allMarkers.filter(m => m.side === 'back'),
    leftChest:  allMarkers.filter(m => m.side === 'leftChest'),
    rightChest: allMarkers.filter(m => m.side === 'rightChest'),
  };

  // One marker per side for now. (Multi-marker layouts on a single
  // side are uncommon and add complexity for marginal value.)
  const maxMarkers = 1;

  function emit(nextMarkers, nextNotes) {
    onChange({ markers: nextMarkers, notes: nextNotes });
  }

  function setSideMarkers(side, sideMarkers) {
    // Reattach the side label so the backend / PDF can dispatch.
    const tagged = sideMarkers.map(m => ({ ...m, side }));
    const otherMarkers = allMarkers.filter(m => (m.side || 'front') !== side);
    emit([...otherMarkers, ...tagged], allNotes);
  }

  function setSideNote(side, text) {
    const nextNotes = { ...allNotes };
    if (text) nextNotes[side] = text;
    else delete nextNotes[side];
    emit(allMarkers, nextNotes);
  }

  function dismissSide(side) {
    setDismissedSides(prev => { const next = new Set(prev); next.add(side); return next; });
    // Clear any markers on that side too.
    setSideMarkers(side, []);
  }

  function restoreSide(side) {
    setDismissedSides(prev => { const next = new Set(prev); next.delete(side); return next; });
  }

  // Only drag-side dismissals are surfaced — text-only sides have no
  // dismiss UX (the customer just leaves the input blank).
  const dismissedSidesList = SIDE_KEYS.filter(side =>
    isDragSide(side) && dismissedSides.has(side) && sides[side] && urlBySide[side] && !failedBySide[side]
  );

  // Only label cards by their side when we have something to show.
  // The front label is contingent on URL pattern detection; the back
  // label always reads "BACK" since it's only shown when the back URL
  // is usable.
  const labelBySide = {
    front: isLabeledFront(frontUrl) ? SIDE_LABELS.front : '',
    back: backUrl ? SIDE_LABELS.back : '',
    leftChest: frontUrl ? SIDE_LABELS.leftChest : '',
    rightChest: frontUrl ? SIDE_LABELS.rightChest : '',
  };

  // Drag sides get canvas cards, text sides get labeled inputs.
  const DRAG_SIDES = ['front', 'back', 'leftChest', 'rightChest'];
  const dragSidesShown = DRAG_SIDES.filter(side => {
    if (!isDragShown(side)) return false;
    // Chest positions share the front photo. Skip them when the
    // assigned logo for that side is missing — without a logo to drop
    // there's no marker to drag, and showing an empty extra "Left
    // Chest" canvas just clutters the editor. Customer opts in by
    // assigning a logo to that side via the dropdowns above.
    if (side === 'leftChest' || side === 'rightChest') {
      return !!logoBySide[side];
    }
    return true;
  });
  const textSidesShown = ['leftSide', 'rightSide'].filter(side => (side === 'leftSide' ? showLeftSide : showRightSide));
  const placeholderBySide = {
    leftSide:  "e.g. 'centered on left sleeve, 3 inches wide, 2 inches from the cuff'",
    rightSide: "e.g. 'centered on right sleeve, 3 inches wide, 2 inches from the cuff'",
  };

  return (
    <div className="placement-item">
      <ItemHeader item={item} />
      <ItemAssignmentRow item={item} itemIndex={itemIndex} logos={logos} onAssignLogo={onAssignLogo} />
      {modeTabs}
      {dragSidesShown.length > 0 && (
        <div className="placement-stage-disclaimer">
          Drag the logo onto the area where you want it printed.
        </div>
      )}
      {dragSidesShown.length > 0 && (
        <div className="placement-grid">
          {dragSidesShown.map(side => {
            // If this side's assigned logo already has a known width
            // from another item's marker, seed the default placement
            // with that width so a freshly-assigned marker matches
            // its siblings instead of jumping to the canvas default.
            const assignedLogoId = item.logoAssignment?.[side];
            const lockedWidth = assignedLogoId && logoWidths
              ? logoWidths[assignedLogoId]
              : null;
            const defaultForSide = lockedWidth != null
              ? { ...DEFAULTS_BY_SIDE[side], widthPct: lockedWidth }
              : DEFAULTS_BY_SIDE[side];
            return (
              <PlacementCanvas
                key={side}
                src={urlBySide[side]}
                label={labelBySide[side]}
                logo={logoBySide?.[side] || null}
                placements={markersBySide[side]}
                defaultPlacement={defaultForSide}
                maxMarkers={maxMarkers}
                onChange={(arr) => setSideMarkers(side, arr)}
                onImageError={() => setFailedBySide[side](true)}
                onDismiss={() => dismissSide(side)}
              />
            );
          })}
        </div>
      )}
      {textSidesShown.length > 0 && (
        <div className="placement-side-notes">
          {textSidesShown.map(side => (
            <div key={side} className="placement-side-note">
              <label className="placement-side-note-label">
                {SIDE_DISPLAY[side]} — describe placement
              </label>
              <textarea
                className="placement-side-note-input"
                rows={2}
                maxLength={300}
                value={allNotes[side] || ''}
                placeholder={placeholderBySide[side]}
                onChange={(e) => setSideNote(side, e.target.value)}
              />
            </div>
          ))}
        </div>
      )}
      {dismissedSidesList.length > 0 && (
        <div className="placement-restore-row">
          {dismissedSidesList.map(side => (
            <button
              key={side}
              type="button"
              className="placement-restore-btn"
              onClick={() => restoreSide(side)}
            >
              + Add {SIDE_DISPLAY[side].toLowerCase()} logo
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PlacementEditor({ items, hasAnyArtwork, placements, onChange, logos, onAssignLogo, logoWidths }) {
  const renderableItems = (items || [])
    .map((item, index) => ({ item, index }));

  if (renderableItems.length === 0) return null;

  return (
    <div className="placement-editor">
      <h4>Assign &amp; Place Logos</h4>
      <p className="placement-help">
        For each item, pick which logo goes on each side (front, back, left side, right side). Drag the marker on the front and back photos to show where it goes; describe sleeve / side placements in words. We'll send a final proof to confirm exact placement and size before printing.
      </p>
      {!hasAnyArtwork && (
        <p className="placement-no-logo-note">
          Upload artwork to a logo above to preview placement. Vector files (AI, EPS, PDF) will show as a labeled "LOGO" box.
        </p>
      )}
      <div className="placement-items">
        {renderableItems.map(({ item, index }) => (
          <ItemPlacementSection
            key={index}
            item={item}
            itemIndex={index}
            placements={placements ? placements[index] : undefined}
            onChange={(arr) =>
              onChange({ ...(placements || {}), [index]: arr })
            }
            logos={logos}
            onAssignLogo={onAssignLogo}
            logoWidths={logoWidths}
          />
        ))}
      </div>
    </div>
  );
}
